import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPen,
  faTrashCan,
  faXmark,
  faCalendarDays,
  faLocationDot,
  faPhone,
  faCircleCheck,
  faTriangleExclamation,
  faRotate,
  faImage,
  faUpload,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import './MyEvents.css';

const API = process.env.REACT_APP_API_BASE_URL;
const imgUrl = (name) => `${process.env.REACT_APP_API_BASE_URL1}/assets/img/events/${name}`;
const auth = (extra = {}) => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}`, ...extra } });

// same fields as before
const EMPTY = {
  EventTitle: '',
  EventSubTitle: '',
  EventDetails: '',
  EventLocation: '',
  EventPlaceName: '',
  EventDate: '',
  EventTime: '',
  EventAddress: '',
  EventPhoneNumber: '',
  Image: '',
};
const MAX_IMAGE = 5 * 1024 * 1024;
const pad = (n) => String(n).padStart(2, '0');

const parse = (s) => {
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDay = (d) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
const fmtTime = (d) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
const daysAway = (d) => Math.round((startOfDay(d) - startOfDay(new Date())) / 86400000);
const awayLabel = (n) => (n === 0 ? 'Today' : n === 1 ? 'Tomorrow' : n > 1 ? `In ${n} days` : n === -1 ? 'Yesterday' : `${-n} days ago`);

/* Event picture with an icon when missing or broken */
const Cover = ({ name, alt }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [name]);
  if (!name || failed) {
    return (
      <span className="rmaevt-cover rmaevt-noimg" aria-hidden="true">
        <FontAwesomeIcon icon={faImage} />
      </span>
    );
  }
  return <img className="rmaevt-cover" src={imgUrl(name)} alt={alt} loading="lazy" onError={() => setFailed(true)} />;
};

const MyEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reload, setReload] = useState(0);
  const [showPast, setShowPast] = useState(false);

  const [mode, setMode] = useState(''); // '' | 'add' | 'edit'
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formData, setFormData] = useState(EMPTY);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [toDelete, setToDelete] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [toast, setToast] = useState('');

  // ---------- Load (same endpoint) ----------
  const fetchEvents = useCallback(async () => {
    const response = await axios.get(`${API}/api/admin/MyEvents/allevents`, auth());
    setEvents(Array.isArray(response.data) ? response.data : []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    fetchEvents()
      .catch((err) => {
        console.error('Error fetching events:', err);
        if (!cancelled) setLoadError('We couldn’t load the events.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchEvents, reload]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // free a picked image's preview
  useEffect(() => {
    const url = imagePreview;
    return () => {
      if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
    };
  }, [imagePreview]);

  // Escape closes; no page scroll behind a window
  const anyOpen = Boolean(mode || toDelete);
  useEffect(() => {
    if (!anyOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (toDelete) {
        if (!deleteBusy) setToDelete(null);
      } else if (!saving) setMode('');
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [anyOpen, toDelete, saving, deleteBusy]);

  // ---------- Form ----------
  const openAdd = () => {
    setFormData(EMPTY);
    setImageFile(null);
    setImagePreview('');
    setSelectedEvent(null);
    setFormError('');
    setMode('add');
  };

  const openEdit = (event) => {
    const d = parse(event.EventDate);
    setSelectedEvent(event);
    setFormData({
      EventTitle: event.EventTitle ?? '',
      EventSubTitle: event.EventSubTitle ?? '',
      EventDetails: event.EventDetails ?? '',
      EventLocation: event.EventLocation ?? '',
      EventPlaceName: event.EventPlaceName ?? '',
      // date AND time from the same (local) clock — the old page mixed UTC date with local time
      EventDate: d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : '',
      EventTime: d ? `${pad(d.getHours())}:${pad(d.getMinutes())}` : '',
      EventAddress: event.EventAddress ?? '',
      EventPhoneNumber: event.EventPhoneNumber ?? '',
      Image: event.Image ?? '',
    });
    setImageFile(null);
    setImagePreview(event.Image ? imgUrl(event.Image) : '');
    setFormError('');
    setMode('edit');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_IMAGE) {
      setFormError('The image must be smaller than 5 MB.');
      return;
    }
    setFormError('');
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setFormData((prev) => ({ ...prev, Image: file.name }));
  };

  // same endpoints + same FormData as before
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    try {
      const fd = new FormData();
      Object.keys(formData).forEach((key) => fd.append(key, formData[key]));
      if (imageFile) fd.append('image', imageFile);
      const opts = auth({ 'Content-Type': 'multipart/form-data' });
      if (mode === 'add') {
        await axios.post(`${API}/api/admin/MyEvents/addevent`, fd, opts);
      } else {
        fd.append('Id', selectedEvent.Id);
        await axios.put(`${API}/api/admin/MyEvents/editevent`, fd, opts);
      }
      await fetchEvents();
      setToast(mode === 'add' ? 'Event added' : 'Event saved');
      setMode('');
    } catch (err) {
      console.error('Error saving event:', err);
      setFormError(err.response?.data?.error || err.response?.data?.message || 'Couldn’t save the event. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ---------- Delete (same endpoint, now really sends { Id }) ----------
  const handleDelete = async () => {
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await axios.delete(`${API}/api/admin/MyEvents/deleteevent`, { ...auth(), data: { Id: toDelete.Id } });
      await fetchEvents();
      setToDelete(null);
      setToast('Event deleted');
    } catch (err) {
      console.error('Error deleting event:', err);
      setDeleteError('Couldn’t delete the event. Please try again.');
    } finally {
      setDeleteBusy(false);
    }
  };

  // ---------- Lists ----------
  const today = startOfDay(new Date());
  const withDate = events.map((ev) => ({ ev, d: parse(ev.EventDate) }));
  const upcoming = withDate.filter((x) => !x.d || startOfDay(x.d) >= today).sort((a, b) => (a.d?.getTime() || 0) - (b.d?.getTime() || 0));
  const past = withDate.filter((x) => x.d && startOfDay(x.d) < today).sort((a, b) => b.d - a.d);

  const renderCard = ({ ev, d }, isPast) => {
    const n = d ? daysAway(d) : null;
    const place = [ev.EventPlaceName, ev.EventAddress, ev.EventLocation].filter(Boolean).join(', ');
    return (
      <li key={ev.Id} className={`rmaevt-card ${isPast ? 'is-past' : ''}`}>
        <div className="rmaevt-media">
          <Cover name={ev.Image} alt={ev.EventTitle} />
          {d && (
            <span className="rmaevt-date" aria-hidden="true">
              <b>{d.getDate()}</b>
              {d.toLocaleDateString('en-US', { month: 'short' })}
            </span>
          )}
          {n !== null && <span className={`rmaevt-away ${n >= 0 && n <= 7 ? 'is-soon' : ''}`}>{awayLabel(n)}</span>}
        </div>
        <div className="rmaevt-body">
          <h3>{ev.EventTitle}</h3>
          {ev.EventSubTitle && <p className="rmaevt-sub">{ev.EventSubTitle}</p>}
          {ev.EventDetails && <p className="rmaevt-text">{ev.EventDetails}</p>}
          <ul className="rmaevt-meta">
            {d && (
              <li>
                <FontAwesomeIcon icon={faCalendarDays} /> {fmtDay(d)}
                <span className="rmaevt-time">
                  <FontAwesomeIcon icon={faClock} /> {fmtTime(d)}
                </span>
              </li>
            )}
            {place && (
              <li>
                <FontAwesomeIcon icon={faLocationDot} /> {place}
              </li>
            )}
            {ev.EventPhoneNumber && (
              <li>
                <FontAwesomeIcon icon={faPhone} /> <a href={`tel:${String(ev.EventPhoneNumber).replace(/[^\d+]/g, '')}`}>{ev.EventPhoneNumber}</a>
              </li>
            )}
          </ul>
        </div>
        <div className="rmaevt-actions">
          <button type="button" onClick={() => openEdit(ev)}>
            <FontAwesomeIcon icon={faPen} /> Edit
          </button>
          <button
            type="button"
            className="is-danger"
            onClick={() => {
              setDeleteError('');
              setToDelete(ev);
            }}
            aria-label={`Delete ${ev.EventTitle}`}
          >
            <FontAwesomeIcon icon={faTrashCan} />
          </button>
        </div>
      </li>
    );
  };

  return (
    <div className="rmaevt">
      <header className="rmaevt-header">
        <div>
          <h1>Events</h1>
          <p>Workshops and events shown on the website.</p>
        </div>
        <button type="button" className="rmaevt-btn" onClick={openAdd}>
          <FontAwesomeIcon icon={faPlus} /> Add event
        </button>
      </header>

      {loading ? (
        <div className="rmaevt-grid" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rmaevt-skel" />
          ))}
        </div>
      ) : loadError ? (
        <div className="rmaevt-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
          <p>{loadError}</p>
          <button type="button" className="rmaevt-btn" onClick={() => setReload((n) => n + 1)}>
            <FontAwesomeIcon icon={faRotate} /> Try again
          </button>
        </div>
      ) : events.length === 0 ? (
        <div className="rmaevt-empty">
          <FontAwesomeIcon icon={faCalendarDays} />
          <p>No events yet.</p>
          <button type="button" className="rmaevt-btn" onClick={openAdd}>
            <FontAwesomeIcon icon={faPlus} /> Add the first event
          </button>
        </div>
      ) : (
        <>
          <h2 className="rmaevt-title">
            Upcoming <span>{upcoming.length}</span>
          </h2>
          {upcoming.length ? (
            <ul className="rmaevt-grid">{upcoming.map((x) => renderCard(x, false))}</ul>
          ) : (
            <p className="rmaevt-none">No upcoming events.</p>
          )}

          {past.length > 0 && (
            <>
              <button type="button" className="rmaevt-toggle" onClick={() => setShowPast((v) => !v)} aria-expanded={showPast}>
                {showPast ? 'Hide' : 'Show'} past events <span>{past.length}</span>
              </button>
              {showPast && <ul className="rmaevt-grid">{past.map((x) => renderCard(x, true))}</ul>}
            </>
          )}
        </>
      )}

      {/* ---------- Add / edit ---------- */}
      {mode && (
        <div className="rmaevt-overlay" onMouseDown={(e) => e.target === e.currentTarget && !saving && setMode('')}>
          <form className="rmaevt-modal" role="dialog" aria-modal="true" aria-labelledby="rmaevt-form-title" onSubmit={handleSubmit}>
            <div className="rmaevt-modal-head">
              <h2 id="rmaevt-form-title">{mode === 'add' ? 'Add event' : 'Edit event'}</h2>
              <button type="button" className="rmaevt-x" onClick={() => setMode('')} disabled={saving} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="rmaevt-modal-body">
              <label className="rmaevt-field">
                <span>Title *</span>
                <input type="text" name="EventTitle" value={formData.EventTitle} onChange={handleInputChange} required autoFocus />
              </label>
              <label className="rmaevt-field">
                <span>Subtitle</span>
                <input type="text" name="EventSubTitle" value={formData.EventSubTitle} onChange={handleInputChange} />
              </label>
              <label className="rmaevt-field">
                <span>Details *</span>
                <textarea name="EventDetails" rows={4} value={formData.EventDetails} onChange={handleInputChange} required />
              </label>
              <div className="rmaevt-two">
                <label className="rmaevt-field">
                  <span>Date *</span>
                  <input type="date" name="EventDate" value={formData.EventDate} onChange={handleInputChange} required />
                </label>
                <label className="rmaevt-field">
                  <span>Time</span>
                  <input type="time" name="EventTime" value={formData.EventTime} onChange={handleInputChange} />
                </label>
              </div>
              <div className="rmaevt-two">
                <label className="rmaevt-field">
                  <span>Place name *</span>
                  <input type="text" name="EventPlaceName" value={formData.EventPlaceName} onChange={handleInputChange} placeholder="e.g. ABC Mall" required />
                </label>
                <label className="rmaevt-field">
                  <span>Location</span>
                  <input type="text" name="EventLocation" value={formData.EventLocation} onChange={handleInputChange} placeholder="e.g. Beirut" />
                </label>
              </div>
              <label className="rmaevt-field">
                <span>Address *</span>
                <input type="text" name="EventAddress" value={formData.EventAddress} onChange={handleInputChange} required />
              </label>
              <label className="rmaevt-field">
                <span>Phone number *</span>
                <input type="tel" name="EventPhoneNumber" value={formData.EventPhoneNumber} onChange={handleInputChange} placeholder="+961 …" required />
              </label>
              <div className="rmaevt-field">
                <span>Image</span>
                <div className="rmaevt-imgpick">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" />
                  ) : (
                    <span className="rmaevt-noimg">
                      <FontAwesomeIcon icon={faImage} />
                    </span>
                  )}
                  <label className="rmaevt-btn rmaevt-btn--ghost">
                    <input type="file" accept="image/*" onChange={handleImageChange} />
                    <FontAwesomeIcon icon={faUpload} /> {imagePreview ? 'Change image' : 'Choose image'}
                  </label>
                  <small>{imageFile ? imageFile.name : mode === 'edit' && imagePreview ? 'Current image — kept unless you choose a new one.' : 'JPG or PNG, up to 5 MB.'}</small>
                </div>
              </div>
              {formError && (
                <p className="rmaevt-error" role="alert">
                  {formError}
                </p>
              )}
            </div>
            <div className="rmaevt-modal-foot">
              <button type="button" className="rmaevt-btn rmaevt-btn--ghost" onClick={() => setMode('')} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="rmaevt-btn" disabled={saving}>
                {saving ? 'Saving…' : mode === 'add' ? 'Add event' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ---------- Delete confirm ---------- */}
      {toDelete && (
        <div className="rmaevt-overlay" onMouseDown={(e) => e.target === e.currentTarget && !deleteBusy && setToDelete(null)}>
          <div className="rmaevt-dialog" role="alertdialog" aria-modal="true" aria-labelledby="rmaevt-del-title">
            <span className="rmaevt-dialog-icon">
              <FontAwesomeIcon icon={faTrashCan} />
            </span>
            <h2 id="rmaevt-del-title">Delete this event?</h2>
            <p>
              <strong>{toDelete.EventTitle}</strong>
              <br />
              It will disappear from the website. This can’t be undone.
            </p>
            {deleteError && (
              <p className="rmaevt-error" role="alert">
                {deleteError}
              </p>
            )}
            <div className="rmaevt-dialog-actions">
              <button type="button" className="rmaevt-btn rmaevt-btn--ghost" onClick={() => setToDelete(null)} disabled={deleteBusy}>
                Cancel
              </button>
              <button type="button" className="rmaevt-btn rmaevt-btn--danger" onClick={handleDelete} disabled={deleteBusy}>
                {deleteBusy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="rmaevt-toast" role="status">
          <FontAwesomeIcon icon={faCircleCheck} /> {toast}
        </div>
      )}
    </div>
  );
};

export default MyEvents;